import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtBRL, fmtInt, STATUS_LABELS } from "@/lib/api";
import {
  TrendUp,
  Factory,
  Warning,
  Trophy,
  CurrencyCircleDollar,
} from "@phosphor-icons/react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Legend } from "recharts";
import { Tile, RangePicker, fmtPeriod, pctDelta } from "@/components/dashboard/DashboardWidgets";

// Estilos comuns dos gráficos — extraídos como constantes pra evitar criar novos objetos a cada render
const TICK_STYLE = { fontSize: 10, fontFamily: "IBM Plex Mono" };
const TICK_STYLE_LG = { fontSize: 11, fontFamily: "IBM Plex Mono" };
const TOOLTIP_STYLE = {
  border: "1px solid var(--line)",
  borderRadius: 6,
  background: "var(--paper)",
  color: "var(--ink)",
  fontFamily: "IBM Plex Mono",
};
const LINE_DOT_STYLE = { fill: "var(--klein)", r: 3 };
const BAR_CHART_MARGIN = { left: 60, right: 20 };

export default function Dashboard() {
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [productivity, setProductivity] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [finance, setFinance] = useState(null);

  // Novos gráficos com filtros
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const defaultCustom = {
    start: iso(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30)),
    end: iso(today),
  };

  const [salesRange, setSalesRange] = useState("day");
  const [salesCustom, setSalesCustom] = useState(defaultCustom);
  const [salesData, setSalesData] = useState({ series: [], granularity: "day", start: null, end: null });
  const [salesLoading, setSalesLoading] = useState(false);

  const [cupRange, setCupRange] = useState("month");
  const [cupCustom, setCupCustom] = useState(defaultCustom);
  const [cupSku, setCupSku] = useState("");
  const [cupData, setCupData] = useState({ rows: [], total_cups: 0, start: null, end: null });
  const [cupLoading, setCupLoading] = useState(false);

  const [colorRange, setColorRange] = useState("month");
  const [colorCustom, setColorCustom] = useState(defaultCustom);
  const [colorSku, setColorSku] = useState("");
  const [colorData, setColorData] = useState({ by_cup_color: [], by_cup_silk: [], start: null, end: null });
  const [colorLoading, setColorLoading] = useState(false);

  useEffect(() => {
    let on = true;
    api.get("/dashboard/metrics").then(({ data }) => {
      if (on) {
        setM(data);
        setLoading(false);
      }
    }).catch(() => setLoading(false));
    api.get("/dashboard/productivity", { params: { days: 7 } })
      .then(({ data }) => { if (on) setProductivity(data.ranking || []); })
      .catch(() => { /* silent */ });
    api.get("/dashboard/shipping-forecast")
      .then(({ data }) => { if (on) setForecast(data); })
      .catch(() => { /* silent */ });
    api.get("/finance/summary")
      .then(({ data }) => { if (on) setFinance(data); })
      .catch(() => { /* silent */ });
    return () => { on = false; };
  }, []);

  // Loaders com filtros
  useEffect(() => {
    const params = { range: salesRange };
    if (salesRange === "custom") { params.start = salesCustom.start; params.end = salesCustom.end; }
    setSalesLoading(true);
    api.get("/dashboard/sales-trend", { params })
      .then(({ data }) => setSalesData(data))
      .catch((e) => console.warn("sales-trend erro:", e?.response?.status))
      .finally(() => setSalesLoading(false));
  }, [salesRange, salesCustom]);

  useEffect(() => {
    const params = { range: cupRange };
    if (cupRange === "custom") { params.start = cupCustom.start; params.end = cupCustom.end; }
    if (cupSku) params.sku = cupSku;
    setCupLoading(true);
    const t = setTimeout(() => {
      api.get("/dashboard/production-by-cup", { params })
        .then(({ data }) => setCupData(data))
        .catch((e) => console.warn("production-by-cup erro:", e?.response?.status))
        .finally(() => setCupLoading(false));
    }, 350); // debounce para SKU typing
    return () => clearTimeout(t);
  }, [cupRange, cupCustom, cupSku]);

  useEffect(() => {
    const params = { range: colorRange };
    if (colorRange === "custom") { params.start = colorCustom.start; params.end = colorCustom.end; }
    if (colorSku) params.sku = colorSku;
    setColorLoading(true);
    const t = setTimeout(() => {
      api.get("/dashboard/production-by-color", { params })
        .then(({ data }) => setColorData(data))
        .catch((e) => console.warn("production-by-color erro:", e?.response?.status))
        .finally(() => setColorLoading(false));
    }, 350);
    return () => clearTimeout(t);
  }, [colorRange, colorCustom, colorSku]);

  if (loading || !m) {
    return (
      <div className="p-12">
        <div className="overline text-zinc-400">CARREGANDO MÉTRICAS</div>
        <div className="font-head text-4xl mt-2">…</div>
      </div>
    );
  }

  // Deltas percentuais para cada comparação
  const deltaToday = pctDelta(m.today?.net || 0, m.yesterday?.net || 0);
  const deltaWeek = pctDelta(m.week?.net || 0, m.prev_week?.net || 0);
  const deltaPrevMonth = pctDelta(m.prev_month?.net || 0, m.prev_prev_month?.net || 0);
  const delta30d = pctDelta(m.net_30d?.net || 0, m.prev_month?.net || 0);
  const grossMargin20 = m.month_gross_margin_20 || (m.month?.total || 0) * 0.20;

  const DeltaBadge = ({ value, testid }) => {
    if (value === null || Number.isNaN(value)) return null;
    const positive = value >= 0;
    return (
      <span
        className={`inline-flex items-center gap-0.5 text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ${positive ? "text-[#00A300] bg-green-50" : "text-[#E60000] bg-red-50"}`}
        data-testid={testid}
      >
        {positive ? "▲" : "▼"} {Math.abs(value).toFixed(1)}%
      </span>
    );
  };

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="overline text-zinc-500 mb-2">PAINEL DE CONTROLE</div>
          <h1 className="font-head text-2xl sm:text-3xl font-black tracking-tight">Dashboard</h1>
        </div>
        <div className="text-right">
          <div className="overline text-zinc-500">DATA</div>
          <div className="font-mono mt-1">{new Date().toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })}</div>
        </div>
      </div>

      {/* Alertas compactos: atrasos + shipping forecast na mesma linha */}
      <div className="grid grid-cols-1 lg:grid-cols-6 gap-[1px] bg-black border border-black mb-6" data-testid="alert-strip">
        <Link
          to="/kanban"
          className={`p-4 flex items-center justify-between ${(m.overdue_count || 0) > 0 ? "bg-[#E60000] text-white" : "bg-white"}`}
          data-testid="overdue-compact"
        >
          <div className="flex items-center gap-3">
            <Warning size={22} weight={m.overdue_count > 0 ? "fill" : "bold"} />
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">Atrasados</div>
              <div className="font-mono text-2xl font-bold leading-none">{fmtInt(m.overdue_count || 0)}</div>
            </div>
          </div>
          <span className="text-[10px] uppercase tracking-widest font-bold">Ver ›</span>
        </Link>
        {(forecast?.buckets || []).map((b) => (
          <div key={b.offset} className="bg-white p-4" data-testid={`forecast-${b.offset}`}>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Enviar {b.label}</div>
            <div className="font-mono text-2xl font-bold leading-none mt-1">{fmtInt(b.count)}</div>
            <div className="text-[10px] text-zinc-400 font-mono mt-1">{b.date.length > 12 ? b.date : new Date(b.date + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })}</div>
          </div>
        ))}
      </div>

      {/* Contas a pagar: hoje / amanhã / 3d / 7d */}
      {finance && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-[1px] bg-black border border-black mb-6" data-testid="finance-strip">
          <Link to="/financeiro/contas-a-pagar" className={`p-4 flex items-center justify-between ${finance.overdue.count > 0 ? "bg-[#E60000] text-white" : "bg-black text-white"}`} data-testid="finance-hero">
            <div className="flex items-center gap-3">
              <CurrencyCircleDollar size={22} weight="bold" />
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">
                  {finance.overdue.count > 0 ? "Contas Vencidas" : "Contas a Pagar"}
                </div>
                <div className="font-mono text-lg font-bold leading-none mt-1">
                  {finance.overdue.count > 0 ? fmtBRL(finance.overdue.total) : "Financeiro"}
                </div>
              </div>
            </div>
            <span className="text-[10px] uppercase tracking-widest font-bold">Ver ›</span>
          </Link>
          {finance.buckets.map((b) => (
            <div key={b.label} className="bg-white p-4" data-testid={`finance-bucket-${b.label.replace(/\s/g, '-').replace('ã', 'a')}`}>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">A pagar {b.label}</div>
              <div className="font-mono text-lg font-bold leading-none mt-1">{fmtBRL(b.total)}</div>
              <div className="text-[10px] text-zinc-400 font-mono mt-1">{b.count} conta{b.count !== 1 ? "s" : ""}</div>
            </div>
          ))}
        </div>
      )}

      {/* Hero metric: Valor Líquido a Receber — últimos 30 dias */}
      <div className="border border-black p-5 md:p-6 mb-6 bg-black text-white" data-testid="hero-net-month">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-end">
          <div className="lg:col-span-2">
            <div className="overline text-white/60 mb-2">VALOR LÍQUIDO A RECEBER · ÚLTIMOS 30 DIAS</div>
            <div className="font-mono text-2xl md:text-3xl lg:text-4xl font-semibold leading-none tracking-tight" data-testid="hero-net-30d">
              {fmtBRL(m.net_30d?.net || 0)}
            </div>
            <div className="mt-3 flex items-center gap-4 text-xs flex-wrap">
              {delta30d !== null && (
                <div className="flex items-center gap-2">
                  <TrendUp size={16} weight="bold" style={{ color: delta30d >= 0 ? "#00E070" : "#FF6060" }} />
                  <span className="font-mono" style={{ color: delta30d >= 0 ? "#00E070" : "#FF6060" }}>
                    {delta30d >= 0 ? "+" : ""}{delta30d.toFixed(1)}%
                  </span>
                  <span className="text-white/60">vs. mês anterior ({fmtBRL(m.prev_month?.net || 0)})</span>
                </div>
              )}
              <span className="text-white/50 font-mono">· {fmtInt(m.net_30d?.count || 0)} pedidos</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="overline text-white/60 mb-1 text-[10px]">PEDIDOS · MÊS ATUAL</div>
              <div className="font-mono text-lg" data-testid="hero-month-count">{fmtInt(m.month?.count || 0)}</div>
              <div className="text-[10px] text-white/40 font-mono mt-1">01 → hoje</div>
            </div>
            <div>
              <div className="overline text-white/60 mb-1 text-[10px]">VALOR BRUTO · MÊS</div>
              <div className="font-mono text-base text-white/80" data-testid="hero-month-gross">{fmtBRL(m.month?.total || 0)}</div>
              <div className="text-[10px] text-[#FFC000] font-mono mt-1" data-testid="hero-margin-20">
                margem 20%: {fmtBRL(grossMargin20)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bento row 1 — comparações diárias/semanais/mensais */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-[1px] bg-black border border-black mb-6">
        <div className="border border-black p-4 bg-white" data-testid="metric-today">
          <div className="overline text-zinc-500 mb-2 flex items-center justify-between">
            <span>LÍQUIDO · HOJE</span>
            <DeltaBadge value={deltaToday} testid="delta-today" />
          </div>
          <div className="font-mono text-lg md:text-xl lg:text-2xl leading-none break-words">
            {fmtBRL(m.today?.net || 0)}
          </div>
          <div className="text-[11px] text-zinc-500 mt-2">
            {fmtInt(m.today?.count || 0)} pedidos · ontem: {fmtBRL(m.yesterday?.net || 0)}
          </div>
        </div>

        <div className="border border-black p-4 bg-white" data-testid="metric-week">
          <div className="overline text-zinc-500 mb-2 flex items-center justify-between">
            <span>LÍQUIDO · SEMANA (7d)</span>
            <DeltaBadge value={deltaWeek} testid="delta-week" />
          </div>
          <div className="font-mono text-lg md:text-xl lg:text-2xl leading-none break-words">
            {fmtBRL(m.week?.net || 0)}
          </div>
          <div className="text-[11px] text-zinc-500 mt-2">
            {fmtInt(m.week?.count || 0)} pedidos · sem. ant.: {fmtBRL(m.prev_week?.net || 0)}
          </div>
        </div>

        <div className="border border-black p-4 bg-white" data-testid="metric-prev-month">
          <div className="overline text-zinc-500 mb-2 flex items-center justify-between">
            <span>LÍQUIDO · MÊS ANT.</span>
            <DeltaBadge value={deltaPrevMonth} testid="delta-prev-month" />
          </div>
          <div className="font-mono text-lg md:text-xl lg:text-2xl leading-none break-words">
            {fmtBRL(m.prev_month?.net || 0)}
          </div>
          <div className="text-[11px] text-zinc-500 mt-2">
            {fmtInt(m.prev_month?.count || 0)} pedidos · retrasado: {fmtBRL(m.prev_prev_month?.net || 0)}
          </div>
        </div>

        <Tile
          label="ESTOQUE TOTAL"
          value={fmtInt(m.inventory_total)}
          hint="copos disponíveis"
          accent="#00A300"
          testid="metric-inventory"
        />
      </div>

      {/* Evolução de Vendas — com filtros */}
      <div className="border border-black bg-white p-5 mb-6" data-testid="chart-sales-trend">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="overline text-zinc-500 flex items-center gap-2">
              EVOLUÇÃO DE VENDAS · LÍQUIDO
              {salesLoading && <span className="text-[10px] normal-case tracking-normal text-[color:var(--klein)] animate-pulse">atualizando…</span>}
            </div>
            <div className="font-head text-xl font-bold mt-1">
              {salesData.granularity === "day" && "Diário — últimos 15 dias"}
              {salesData.granularity === "week" && "Semanal — últimas 14 semanas"}
              {salesData.granularity === "month" && "Mensal — últimos 12 meses"}
            </div>
            {salesData.start && (
              <div className="text-[11px] font-mono text-zinc-500 mt-1" data-testid="sales-period">
                {fmtPeriod(salesData.start, salesData.end)} · {salesData.series?.length || 0} pontos
              </div>
            )}
          </div>
          <RangePicker value={salesRange} onChange={setSalesRange} custom={salesCustom} onCustomChange={setSalesCustom} testid="sales-range" />
        </div>
        <div style={{ width: "100%", height: 240, minWidth: 0 }}>
          <ResponsiveContainer width="99%" height={240} minWidth={0}>
            <LineChart data={salesData.series}>
              <CartesianGrid strokeDasharray="2 4" stroke="var(--line)" />
              <XAxis dataKey="bucket" tick={TICK_STYLE} stroke="var(--ink)" />
              <YAxis tick={TICK_STYLE} stroke="var(--ink)" />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v, name) => name === "count" ? `${fmtInt(v)} pedidos` : fmtBRL(v)} />
              <Line type="monotone" dataKey="net" stroke="var(--klein)" strokeWidth={2.5} dot={LINE_DOT_STYLE} />
              <Line type="monotone" dataKey="total" stroke="var(--sub)" strokeWidth={1} strokeDasharray="4 3" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Produção por Copo — barras horizontais com filtros */}
      <div className="border border-black bg-white p-5 mb-6" data-testid="chart-production-by-cup">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Factory size={24} weight="bold" />
            <div>
              <div className="overline text-zinc-500 flex items-center gap-2">
                PRODUÇÃO · COPOS POR TIPO
                {cupLoading && <span className="text-[10px] normal-case tracking-normal text-[color:var(--klein)] animate-pulse">atualizando…</span>}
              </div>
              <div className="font-head text-xl font-bold mt-1">
                Total: <span className="font-mono text-[#00A300]">{fmtInt(cupData.total_cups || 0)}</span> copos
              </div>
              {cupData.start && (
                <div className="text-[11px] font-mono text-zinc-500 mt-1" data-testid="cup-period">
                  {fmtPeriod(cupData.start, cupData.end)} · {cupData.rows?.length || 0} SKU(s)
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="text"
              placeholder="Filtrar SKU (ex: POP050)"
              value={cupSku}
              onChange={(e) => setCupSku(e.target.value)}
              className="input !py-1 !text-xs !w-[160px] font-mono"
              data-testid="cup-sku-filter"
            />
            <RangePicker value={cupRange} onChange={setCupRange} custom={cupCustom} onCustomChange={setCupCustom} testid="cup-range" />
          </div>
        </div>
        {(cupData.rows || []).length === 0 ? (
          <div className="text-center text-zinc-400 py-8 text-sm">Nenhum pedido enviado no período selecionado.</div>
        ) : (
          <div style={{ width: "100%", height: Math.max(180, (cupData.rows || []).length * 36 + 40), minWidth: 0 }}>
            <ResponsiveContainer width="99%" height="100%" minWidth={0}>
              <BarChart data={cupData.rows} layout="vertical" margin={BAR_CHART_MARGIN}>
                <CartesianGrid strokeDasharray="2 4" stroke="var(--line)" />
                <XAxis type="number" tick={TICK_STYLE} stroke="var(--ink)" />
                <YAxis type="category" dataKey="sku" tick={TICK_STYLE_LG} stroke="var(--ink)" width={80} />
                <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => `${fmtInt(v)} copos`} labelFormatter={(l, p) => p?.[0]?.payload?.name || l} />
                <Bar dataKey="cups" fill="var(--klein)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Produção por Cor — separado por tipo de copo + silk por tipo */}
      <div className="border border-black bg-white p-5 mb-6" data-testid="chart-production-by-color">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="overline text-zinc-500 flex items-center gap-2">
              PRODUÇÃO · CORES POR TIPO DE COPO
              {colorLoading && <span className="text-[10px] normal-case tracking-normal text-[color:var(--klein)] animate-pulse">atualizando…</span>}
            </div>
            <div className="font-head text-xl font-bold mt-1">Copos e Silk usados</div>
            <div className="text-[11px] text-zinc-500 mt-1">Apenas pedidos que passaram pelo Kanban até <strong>enviado</strong>.</div>
            {colorData.start && (
              <div className="text-[11px] font-mono text-zinc-500 mt-1" data-testid="color-period">
                {fmtPeriod(colorData.start, colorData.end)} · {colorData.by_cup_color?.length || 0} tipos
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="text"
              placeholder="Filtrar SKU"
              value={colorSku}
              onChange={(e) => setColorSku(e.target.value)}
              className="input !py-1 !text-xs !w-[140px] font-mono"
              data-testid="color-sku-filter"
            />
            <RangePicker value={colorRange} onChange={setColorRange} custom={colorCustom} onCustomChange={setColorCustom} testid="color-range" />
          </div>
        </div>

        {/* Copos por cor */}
        <div className="mb-6">
          <div className="overline text-zinc-500 mb-3 text-[10px]">COPOS · COR DO CORPO</div>
          {(colorData.by_cup_color || []).length === 0 ? (
            <div className="text-center text-zinc-400 py-6 text-sm">Sem dados no período.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="colors-by-cup-list">
              {colorData.by_cup_color.map((row) => (
                <div key={row.sku} className="border border-zinc-200 rounded-md p-3" data-testid={`cup-color-${row.sku}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="font-mono text-[11px] text-zinc-500">{row.sku}</div>
                      <div className="font-head font-bold text-sm truncate max-w-[220px]" title={row.type}>{row.type}</div>
                    </div>
                    <div className="font-mono text-lg font-semibold" style={{ color: "var(--klein)" }}>{fmtInt(row.total)}</div>
                  </div>
                  <div className="space-y-1">
                    {row.colors.map((c) => (
                      <div key={c.label} className="flex justify-between items-center text-xs">
                        <span className="text-zinc-600">{c.label}</span>
                        <span className="font-mono font-bold">{fmtInt(c.qty)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Silk por cor por tipo */}
        <div>
          <div className="overline text-zinc-500 mb-3 text-[10px]">SILK · TINTA USADA POR TIPO DE COPO</div>
          {(colorData.by_cup_silk || []).length === 0 ? (
            <div className="text-center text-zinc-400 py-6 text-sm">Sem dados no período.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="silks-by-cup-list">
              {colorData.by_cup_silk.map((row) => (
                <div key={row.sku} className="border border-zinc-200 rounded-md p-3" data-testid={`cup-silk-${row.sku}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="font-mono text-[11px] text-zinc-500">{row.sku}</div>
                      <div className="font-head font-bold text-sm truncate max-w-[220px]" title={row.type}>{row.type}</div>
                    </div>
                    <div className="font-mono text-lg font-semibold" style={{ color: "var(--warn)" }}>{fmtInt(row.total)}</div>
                  </div>
                  <div className="space-y-1">
                    {row.silks.map((s) => (
                      <div key={s.label} className="flex justify-between items-center text-xs">
                        <span className="text-zinc-600">{s.label}</span>
                        <span className="font-mono font-bold">{fmtInt(s.qty)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Status counts */}
      <div className="grid grid-cols-2 md:grid-cols-7 gap-[1px] bg-black border border-black" data-testid="status-grid">
        {["em_desenvolvimento","separacao","gravacao_tela","serigrafia","esteira","expedicao","enviado"].map((k) => (
          <div key={k} className="bg-white p-5">
            <div className="flex items-center gap-2 mb-3">
              <span className={`dot ${k}`} />
              <span className="overline text-zinc-500 text-[10px]">{STATUS_LABELS[k]}</span>
            </div>
            <div className="font-mono text-3xl font-semibold">{fmtInt(m.status_counts?.[k] || 0)}</div>
            <div className="text-xs text-zinc-400 mt-1">pedidos</div>
          </div>
        ))}
      </div>

      {/* Ranking de produtividade (últimos 7 dias) */}
      <div className="mt-6 border border-black bg-white p-6" data-testid="productivity-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Trophy size={22} weight="bold" />
            <div>
              <div className="overline text-zinc-500">RANKING DE PRODUÇÃO</div>
              <h3 className="font-head text-xl font-black">Movimentações no Kanban</h3>
            </div>
          </div>
          <div className="text-xs text-zinc-500">Últimos 7 dias · Pedidos únicos no mês</div>
        </div>
        {productivity.length === 0 ? (
          <div className="text-sm text-zinc-400 text-center py-6">Nenhuma movimentação registrada.</div>
        ) : (
          <div className="space-y-1">
            {/* Cabeçalho */}
            <div className="grid grid-cols-12 gap-3 pb-2 border-b border-black text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              <div className="col-span-1 text-center">#</div>
              <div className="col-span-3">Colaborador</div>
              <div className="col-span-4">Copos movimentados (7 dias)</div>
              <div className="col-span-1 text-right">Copos</div>
              <div className="col-span-1 text-right">Mov.</div>
              <div className="col-span-2 text-right">Pedidos no mês</div>
            </div>
            {productivity.map((row, i) => {
              const max = productivity[0]?.cups || 1;
              const pct = row.cups > 0 ? Math.max(4, Math.round((row.cups / max) * 100)) : 0;
              const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i + 1}º`;
              return (
                <div key={row.name} className="grid grid-cols-12 gap-3 items-center py-2 border-b border-zinc-100 last:border-b-0" data-testid={`productivity-row-${row.name}`}>
                  <div className="col-span-1 text-lg font-bold text-center">{medal}</div>
                  <div className="col-span-3 font-semibold truncate">{row.name}</div>
                  <div className="col-span-4 relative">
                    <div className="h-5 bg-zinc-100 relative overflow-hidden">
                      {pct > 0 && <div className="absolute inset-y-0 left-0 bg-black" style={{ width: `${pct}%` }} />}
                    </div>
                  </div>
                  <div className="col-span-1 text-right font-mono font-bold">{fmtInt(row.cups)}</div>
                  <div className="col-span-1 text-right text-xs text-zinc-500 font-mono">{row.movements}</div>
                  <div className="col-span-2 text-right font-mono font-bold text-[#002FA7]" data-testid={`month-orders-${row.name}`}>
                    {fmtInt(row.month_orders || 0)} <span className="text-[10px] text-zinc-400 font-normal">pedidos</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
